import { KeyIcon, PencilIcon, EyeIcon, DocumentTextIcon, ExclamationTriangleIcon, ArrowPathIcon, DocumentArrowDownIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "components/common/LoadingSpinner";
import SelectedDocument from "../SelectedDocument";
import { Customer } from "types/customer";
import { Quotation } from "types/quotation";
import { useLanguage } from "contexts/LanguageContext";
import { Template } from "types/template";
import { useRef, useEffect, useState } from "react";
import { renderAsync } from 'docx-preview';
import toast from "react-hot-toast";
import { error } from "console";
import { useStorage } from "hooks/useStorage";
import { DocGenerator } from "utils/docGenerator";
import DocumentPreview from "../DocumentPreview";
import PodTableViewer from "../PodTableViewer";

interface EditDataPageProps {
  documentDataProvider: Quotation | Customer | null;
  sortedDocumentData: string[][];
  filledPercentage: number;
  filledCount: number;
  templateFields: string[];
  documentData: Record<string, string>;
  editingKey: string;
  editingValue: string;
  setEditingValue: (value: string) => void;
  handleSaveEdit: () => void;
  handleCancelEdit: () => void;
  handleEdit: (key: string, value: string) => void;
  selectedTemplate: Template | null;
  closePreview: () => void;
  onAutoSavePendingEdits?: () => void; // Callback to auto-save before navigation
}

// Fogyasztás mező formázása ezres tagolással (csak megjelenítéshez)
const formatConsumption = (value: string): string => {
  if (!value) return '';
  
  // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
  const cleanValue = value.replace(/[^0-9.]/g, '');
  
  // Szétválasztjuk a tizedes rész előtti és utáni részre
  const parts = cleanValue.split('.');
  
  // Formázzuk az egész részt ezres tagolással
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Összerakjuk az eredményt
  return parts.length > 1 ? parts.join('.') : parts[0];
};

// Ellenőrzi, hogy a mező fogyasztással kapcsolatos-e
const isConsumptionField = (key: string): boolean => {
  return key === 'Fogyasztás kWh' || 
         key.includes('MÉF') || 
         key.includes('Éves mennyiség') ||
         key.includes('fogyasztás') ||
         key === 'Szerződéses ár' ||
         key === 'Ajánlati ár';
};

// Ellenőrzi, hogy a mező fizetési határidő-e
const isPaymentDueField = (key: string): boolean => {
  return key === 'Fizetési határidő';
};

// Ellenőrzi, hogy a mező az ajánlati/szerződéses ár egysége-e
const isPriceUnitField = (key: string): boolean => {
  return key === 'Ajánlati ár HUF/kWh-EUR/ MWh' || 
         key === 'Ajánlati ár HUF/kWh-EUR/MWh' || 
         key === 'Ár egység' ||
         key === 'priceUnit';
};

// Fizetési határidő értékének előkészítése szerkesztéshez
const preparePaymentDueForEdit = (value: string): string => {
  if (!value) return '';
  return value.replace(/ nap$/, '');
};

// Ajánlati ár egységének előkészítése szerkesztéshez (ha szükséges)
const preparePriceUnitForEdit = (value: string): string => {
  if (!value) return '';
  return value;
};

const EditDataPage = ({
  documentDataProvider,
  sortedDocumentData,
  filledPercentage,
  filledCount,
  templateFields,
  documentData,
  editingKey,
  editingValue,
  setEditingValue,
  handleSaveEdit,
  handleCancelEdit,
  handleEdit,
  selectedTemplate,
  closePreview
}: EditDataPageProps) => {
  const { t } = useLanguage();
  const { uploadFile } = useStorage();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isPodTableViewerOpen, setIsPodTableViewerOpen] = useState(false);
  const [filteredTemplateFields, setFilteredTemplateFields] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const previewFileNameRef = useRef<string>(`preview_${Date.now()}.docx`);

  // Auto-save function for external use (e.g., navigation buttons)
  const autoSavePendingEdits = () => {
    if (editingKey && editingValue) {
      console.log('🔄 Auto-saving pending edit before navigation:', editingKey, '=', editingValue);
      handleSaveEdit();
      return true; // Indicate that something was saved
    }
    return false; // Nothing to save
  };

  // Expose auto-save function globally for navigation buttons
  useEffect(() => {
    // Store the function globally so navigation buttons can call it
    (window as any).autoSavePendingEdits = autoSavePendingEdits;
    
    // Cleanup on unmount
    return () => {
      delete (window as any).autoSavePendingEdits;
    };
  }, [editingKey, editingValue, handleSaveEdit]);

  // Custom edit handler to remove "nap" suffix from Fizetési határidő
  const handleEditField = (key: string, value: string) => {
    let cleanValue = value;
    
    // Remove "nap" from Fizetési határidő value if it appears at the end
    if (isPaymentDueField(key) && typeof value === 'string' && value.trim().endsWith(' nap')) {
      cleanValue = value.replace(/ nap$/, '');
    }
    
    // Special handling for price unit field if needed
    if (isPriceUnitField(key)) {
      console.log("Editing price unit field:", key, value);
      console.log("Raw value type:", typeof value, "Raw value:", value);
      
      // Try to find a valid price unit value from any of the possible fields
      const priceUnitKeys = ['priceUnit', 'Ajánlati ár HUF/kWh-EUR/MWh', 'Ajánlati ár HUF/kWh-EUR/ MWh'];
      let foundValue = value;
      
      // Check if current value is valid
      if (value === 'HUF/kWh' || value === 'EUR/MWh') {
        console.log("Valid price unit value found in current field:", value);
        foundValue = value;
      } 
      // Otherwise check if value exists in any of the other price unit fields
      else {
        for (const priceKey of priceUnitKeys) {
          if (documentData[priceKey] && 
              (documentData[priceKey] === 'HUF/kWh' || documentData[priceKey] === 'EUR/MWh')) {
            foundValue = documentData[priceKey];
            console.log(`Found valid price unit value in ${priceKey}:`, foundValue);
            break;
          }
        }
      }
      
      // If we found a valid value, use it
      if (foundValue === 'HUF/kWh' || foundValue === 'EUR/MWh') {
        console.log("Using valid price unit value:", foundValue);
        cleanValue = foundValue;
        // Set the editing value directly to ensure it's selected in the dropdown
        setTimeout(() => setEditingValue(foundValue), 0);
      } 
      // If no valid value was found, default to HUF/kWh
      else {
        console.log("No valid price unit value found, defaulting to HUF/kWh");
        cleanValue = 'HUF/kWh';
        // Set the editing value directly to ensure it's selected in the dropdown
        setTimeout(() => setEditingValue('HUF/kWh'), 0);
      }
    }
    
    handleEdit(key, cleanValue);
  };

  // Filter out POD_TABLE from display and apply search filter
  useEffect(() => {
    let filtered = templateFields.filter(key => 
      key !== 'POD_TABLE' && 
      key !== 'K épviselő neve' // Szűrjük ki a hibás képviselő mezőt
    );
    
    // Always include Fizetési határidő if documentData exists for it
    const hasPaymentDue = documentData['Fizetési határidő'] && documentData['Fizetési határidő'].trim() !== '';
    const hasPaymentDueField = filtered.includes('Fizetési határidő');
    
    if (hasPaymentDue && !hasPaymentDueField) {
      filtered.push('Fizetési határidő');
    }
    
    // Always include Szerződés száma for contracts (whether it has a value or not)
    // Check if this is a contract by looking for contract-specific indicators
    const isContract = selectedTemplate?.name?.toLowerCase().includes('szerződés') || 
                      selectedTemplate?.name?.toLowerCase().includes('contract') ||
                      // Check if the current page is editing a contract (by checking if contract-specific data exists)
                      (documentDataProvider && typeof documentDataProvider === 'object' && 'customerId' in documentDataProvider) ||
                      // Or if template contains contract-specific fields
                      templateFields.some(field => field.includes('Szerződés')) ||
                      // Or if the documentData already contains a contract number (even if empty)
                      Object.keys(documentData).includes('Szerződés száma');
    
    const hasContractNumberField = filtered.includes('Szerződés száma');
    
    if (isContract && !hasContractNumberField) {
      console.log('Adding Szerződés száma field for contract. Template:', selectedTemplate?.name, 'DocumentDataProvider:', documentDataProvider);
      filtered.push('Szerződés száma');
    }
    
    // Force add contract-specific fields if template name contains "szerződés" or "contract"
    // This is a safety net to ensure contract fields always appear for contract templates
    if (selectedTemplate?.name?.toLowerCase().includes('szerződés') || 
        selectedTemplate?.name?.toLowerCase().includes('contract')) {
      
      console.log('🎯 Force adding contract fields for template:', selectedTemplate?.name);
      
      // Add contract number field - ALWAYS for contracts
      if (!filtered.includes('Szerződés száma')) {
        console.log('✅ Force adding Szerződés száma field due to template name:', selectedTemplate?.name);
        filtered.push('Szerződés száma');
      } else {
        console.log('ℹ️ Szerződés száma field already in filtered list');
      }
      
      // Add contract price field - ALWAYS for contracts
      if (!filtered.includes('Szerződéses ár')) {
        console.log('✅ Force adding Szerződéses ár field due to template name:', selectedTemplate?.name);
        filtered.push('Szerződéses ár');
      } else {
        console.log('ℹ️ Szerződéses ár field already in filtered list');
      }
      
      // Add other contract-specific fields - ALWAYS for contracts, not just if they exist in documentData
      const contractFields = ['Ár egység', 'Fogyasztás', 'Szerződés kezdete', 'Szerződés vége'];
      contractFields.forEach(field => {
        if (!filtered.includes(field)) {
          console.log(`✅ Force adding ${field} field due to contract template:`, selectedTemplate?.name);
          filtered.push(field);
        } else {
          console.log(`ℹ️ ${field} field already in filtered list`);
        }
      });
    }
    
    // Always include price unit field if it exists in the document data
    const priceUnitKey = 'Ajánlati ár HUF/kWh-EUR/ MWh';
    const altPriceUnitKey = 'Ajánlati ár HUF/kWh-EUR/MWh';
    const directPriceUnitKey = 'priceUnit';
    
    const hasPriceUnit = documentData[priceUnitKey] !== undefined || 
                        documentData[altPriceUnitKey] !== undefined || 
                        documentData[directPriceUnitKey] !== undefined;
    
    const hasPriceUnitField = filtered.includes(priceUnitKey) || 
                             filtered.includes(altPriceUnitKey) || 
                             filtered.includes(directPriceUnitKey);
    
    if (hasPriceUnit && !hasPriceUnitField) {
      // Check which key exists in document data
      if (documentData[priceUnitKey] !== undefined) {
        filtered.push(priceUnitKey);
      } else if (documentData[altPriceUnitKey] !== undefined) {
        filtered.push(altPriceUnitKey);
      } else if (documentData[directPriceUnitKey] !== undefined) {
        filtered.push(directPriceUnitKey);
      }
    }
    
    if (searchQuery) {
      filtered = filtered.filter(key => 
        key.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (documentData[key] && documentData[key].toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Debug logging
    console.log('Template fields:', templateFields);
    console.log('Document data keys:', Object.keys(documentData));
    console.log('Payment due field value:', documentData['Fizetési határidő']);
    console.log('Contract number field value:', documentData['Szerződés száma']);
    console.log('Price unit field value:', documentData['Ajánlati ár HUF/kWh-EUR/ MWh']);
    console.log('Alt price unit field value:', documentData['Ajánlati ár HUF/kWh-EUR/MWh']);
    console.log('Direct price unit field value:', documentData['priceUnit']);
    console.log('Is contract template:', isContract);
    console.log('Template name:', selectedTemplate?.name);
    console.log('Filtered fields:', filtered);
    
    // FINAL WORKAROUND: Ensure Szerződés száma is ALWAYS first for contracts
    const finalIsContract = selectedTemplate?.name?.toLowerCase().includes('szerződés') || 
                           selectedTemplate?.name?.toLowerCase().includes('contract');
    if (finalIsContract && !filtered.includes('Szerződés száma')) {
      console.log('🚨 FINAL WORKAROUND: Adding Szerződés száma to the beginning of filtered fields');
      filtered.unshift('Szerződés száma'); // Add to the beginning
    }
    
    console.log('Final filtered fields:', filtered);
    
    setFilteredTemplateFields(filtered);
  }, [templateFields, searchQuery, documentData, selectedTemplate]);

  // Auto-populate contract-specific fields when contract template is selected
  useEffect(() => {
    const isContract = selectedTemplate?.name?.toLowerCase().includes('szerződés') || 
                      selectedTemplate?.name?.toLowerCase().includes('contract');
    
    console.log('🔄 Auto-populate useEffect triggered');
    console.log('  - isContract:', isContract);
    console.log('  - selectedTemplate:', selectedTemplate?.name);
    console.log('  - documentDataProvider:', documentDataProvider);
    console.log('  - documentData keys:', Object.keys(documentData));
    
    console.log('🔍 Checking contract conditions:');
    console.log('  - isContract:', isContract);
    console.log('  - documentDataProvider exists:', !!documentDataProvider);
    console.log('  - documentDataProvider type:', typeof documentDataProvider);
    console.log('  - customerId in documentDataProvider:', documentDataProvider && 'customerId' in documentDataProvider);
    console.log('  - documentDataProvider keys:', documentDataProvider ? Object.keys(documentDataProvider) : 'N/A');
    
    if (isContract && documentDataProvider && typeof documentDataProvider === 'object') {
      const quotation = documentDataProvider as any;
      
      console.log('✅ Contract conditions met, proceeding with auto-populate');
      
      // Try to get quotation data from documentData first (preferred method)
      const quotationNumber = documentData['Ajánlat száma'] || '';
      const contractPrice = documentData['Szerződéses ár'] || documentData['Ajánlati ár'] || '';
      const priceUnit = documentData['Ár egység'] || documentData['Ajánlati ár HUF/kWh-EUR/MWh'] || documentData['priceUnit'] || '';
      const consumption = documentData['Fogyasztás'] || documentData['Fogyasztás kWh'] || '';
      
      console.log('  - Available data from documentData:');
      console.log('    - quotationNumber (Ajánlat száma):', quotationNumber);
      console.log('    - contractPrice (Szerződéses ár/Ajánlati ár):', contractPrice);
      console.log('    - priceUnit (Ár egység/etc):', priceUnit);
      console.log('    - consumption (Fogyasztás/Fogyasztás kWh):', consumption);
      
      // Fallback to quotation object properties
      console.log('  - Fallback from quotation object:');
      console.log('    - quotation.quotationNumber:', quotation.quotationNumber);
      console.log('    - quotation.number:', quotation.number);  
      console.log('    - quotation.price:', quotation.price);
      console.log('    - quotation.priceUnit:', quotation.priceUnit);
      console.log('    - quotation.consumption:', quotation.consumption);
      
      // Use setTimeout to avoid React update timing issues
      setTimeout(() => {
        // Collect all auto-filled fields for a single toast
        const autoFilledFields = [];
        
        // Auto-set contract number if empty
        if (!documentData['Szerződés száma'] || documentData['Szerződés száma'].trim() === '') {
          // Try to get quotation number from documentData first, then from quotation object
          const sourceQuotationNumber = documentData['Ajánlat száma'] || quotation.quotationNumber || quotation.number;
          
          if (sourceQuotationNumber && sourceQuotationNumber.includes('-A-')) {
            // Generate contract number from quotation number by replacing A with S
            const contractNumber = sourceQuotationNumber.replace('-A-', '-S-');
            console.log('🔧 Auto-setting contract number:', contractNumber, 'from source:', sourceQuotationNumber);
            
            // Set the contract number in edit mode
            handleEdit('Szerződés száma', contractNumber);
            autoFilledFields.push('Szerződésszám');
          } else {
            console.log('❌ No valid quotation number found for contract generation');
          }
        }
        
        // Auto-set contract price if empty but data is available
        if (!documentData['Szerződéses ár'] || documentData['Szerződéses ár'].trim() === '') {
          const sourcePrice = contractPrice || quotation.price;
          if (sourcePrice) {
            console.log('🔧 Auto-setting contract price:', sourcePrice);
            handleEdit('Szerződéses ár', sourcePrice.toString());
            autoFilledFields.push('Szerződéses ár');
          }
        }
        
        // Auto-set price unit if empty but data is available
        if (!documentData['Ár egység'] || documentData['Ár egység'].trim() === '') {
          const sourcePriceUnit = priceUnit || quotation.priceUnit;
          if (sourcePriceUnit) {
            console.log('🔧 Auto-setting price unit:', sourcePriceUnit);
            handleEdit('Ár egység', sourcePriceUnit);
            autoFilledFields.push('Ár egység');
          }
        }
        
        // Auto-set consumption if empty but data is available
        if (!documentData['Fogyasztás'] || documentData['Fogyasztás'].trim() === '') {
          const sourceConsumption = consumption || quotation.consumption;
          if (sourceConsumption) {
            console.log('🔧 Auto-setting consumption:', sourceConsumption);
            handleEdit('Fogyasztás', sourceConsumption.toString());
            autoFilledFields.push('Fogyasztás');
          }
        }
        
        // Show single toast for all auto-filled fields
        if (autoFilledFields.length > 0) {
          const fieldsList = autoFilledFields.join(', ');
          toast(`🎯 ${fieldsList} automatikusan kitöltve! Kattints a ✓ pipára az elfogadáshoz.`, {
            duration: 5000,
            position: 'top-center',
            style: {
              background: '#10B981',
              color: 'white',
            },
          });
        }
      }, 200);
    } else {
      console.log('❌ Contract conditions not met');
    }
  }, [selectedTemplate, documentDataProvider, documentData]);

  // Clean the editingValue when it's the Fizetési határidő field
  useEffect(() => {
    if (isPaymentDueField(editingKey) && typeof editingValue === 'string' && editingValue.trim().endsWith(' nap')) {
      const cleanValue = editingValue.replace(/ nap$/, '');
      setEditingValue(cleanValue);
    }
    
    // Log the current editing key and value for debugging
    if (editingKey) {
      console.log("Currently editing:", editingKey, "with value:", editingValue);
      
      // Special handling for price unit field
      if (isPriceUnitField(editingKey)) {
        console.log("Editing price unit field with value:", editingValue);
      }
    }
  }, [editingKey, editingValue]);

  const generatePreview = async () => {
    if (!selectedTemplate || !selectedTemplate.fileUrl) return;

    // Create updated documentData with any pending edits
    let updatedDocumentData = { ...documentData };
    
    // Include current editing field if there's one
    if (editingKey && editingValue) {
      console.log('🔄 Including pending edit in preview:', editingKey, '=', editingValue);
      updatedDocumentData[editingKey] = editingValue;
      
      // Also save the edit for the UI state (so it's saved after preview)
      handleSaveEdit();
    }
    
    // Special handling: Include all auto-filled contract fields even if they're in edit mode elsewhere
    const contractFields = ['Szerződés száma', 'Szerződéses ár', 'Ár egység', 'Fogyasztás'];
    let hasContractUpdates = false;
    
    contractFields.forEach(field => {
      // If field is empty but we have a value from auto-populate, include it
      if (!updatedDocumentData[field] || updatedDocumentData[field].trim() === '') {
        const quotation = documentDataProvider as any;
        if (quotation) {
          let autoValue = null;
          
          // Auto-generate contract number
          if (field === 'Szerződés száma') {
            const sourceQuotationNumber = documentData['Ajánlat száma'] || quotation.quotationNumber || quotation.number;
            if (sourceQuotationNumber && sourceQuotationNumber.includes('-A-')) {
              autoValue = sourceQuotationNumber.replace('-A-', '-S-');
            }
          }
          // Auto-fill price
          else if (field === 'Szerződéses ár') {
            autoValue = quotation.price?.toString();
          }
          // Auto-fill price unit
          else if (field === 'Ár egység') {
            autoValue = quotation.priceUnit;
          }
          // Auto-fill consumption
          else if (field === 'Fogyasztás') {
            autoValue = quotation.consumption?.toString();
          }
          
          if (autoValue) {
            console.log(`🔧 Auto-including ${field} in preview:`, autoValue);
            updatedDocumentData[field] = autoValue;
            hasContractUpdates = true;
          }
        }
      }
    });
    
    if (hasContractUpdates) {
      console.log('📋 Auto-filled contract data included in preview');
    }

    setIsGeneratingPreview(true);
    setIsLoadingPreview(true);

    try {
      // Check specific contract fields for debugging
      console.log('🔍 Checking contract fields in updatedDocumentData:');
      console.log('  Szerződés száma:', updatedDocumentData['Szerződés száma']);
      console.log('  Szerződéses ár:', updatedDocumentData['Szerződéses ár']);
      console.log('  Ár egység:', updatedDocumentData['Ár egység']);
      console.log('  Fogyasztás:', updatedDocumentData['Fogyasztás']);
      
      // Generálunk egy dokumentumot a behelyettesített értékekkel (including pending edits)
      const dataToUse = [updatedDocumentData];

      const generatedDocs = await DocGenerator.generateDocuments(
        selectedTemplate,
        dataToUse,
        ['docx']
      );

      if (generatedDocs['docx'] && generatedDocs['docx'].length > 0) {
        const generatedDocumentBlob = generatedDocs['docx'][0];
        // Új fájlnév-referencia generálása
        previewFileNameRef.current = `preview_${Date.now()}.docx`;

        // Feltöltjük a generált dokumentumot
        
        const fileUrl = await uploadFile(
          new File([generatedDocumentBlob], previewFileNameRef.current, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }),
          'previews'
        );

        if (fileUrl) {
          // Közvetlenül a Firebase Storage URL-t használjuk
          setPreviewUrl(fileUrl);
          setShowPreview(true);
        } else {
          throw new Error("Nem sikerült feltölteni az előnézeti dokumentumot");
        }
      } else {
        throw new Error("Nem sikerült generálni az előnézeti dokumentumot");
      }
    } catch (error) {
      console.error("Hiba az előnézet generálása közben:", error);
      toast.error(t("workPage.messages.previewError"));
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Handle opening the POD table viewer
  const handleOpenPodTableViewer = () => {
    if (documentData['POD_TABLE']) {
      setIsPodTableViewerOpen(true);
    } else {
      toast.error(t("documents.noPodTableData"));
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-auto lg:h-[650px]">
      {/* Értékek szerkesztése panel */}
      <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 flex-1 flex flex-col max-h-full mb-4 lg:mb-0 shadow-sm">
        {documentDataProvider && <SelectedDocument documentDataProvider={documentDataProvider} />}
        <h3 className="text-sm font-medium text-gray-800 mb-2 sm:mb-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
          <span>{t('documents.editValues')}</span>
          <div className="flex items-center space-x-3 text-xs">
            <div className="flex items-center text-gray-500">
              <KeyIcon className="h-3.5 w-3.5 mr-1" />
              <span>{t('documents.fieldsInTemplate', { count: sortedDocumentData.length })}</span>
            </div>
            <div className="flex items-center">
              <span className={`${filledPercentage > 70 ? 'text-green-600' : filledPercentage > 30 ? 'text-amber-600' : 'text-red-600'}`}>
                {filledCount} / {sortedDocumentData.length} {t('documents.filled')} ({filledPercentage}%)
              </span>
            </div>
          </div>
        </h3>

        {/* POD Table Button and Search */}
        <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            placeholder={t('documents.searchField')}
            className="flex-1 bg-gray-100 border border-gray-200 rounded px-3 py-2 text-sm text-gray-800 placeholder-gray-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          {documentData['POD_TABLE'] && (
            <button
              onClick={handleOpenPodTableViewer}
              className="flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            >
              <TableCellsIcon className="h-4 w-4 mr-1" />
              <span className="text-sm">{t('documents.viewPodTable')}</span>
            </button>
          )}
        </div>

        {/* Mező-érték lista */}
        <div className="flex-1 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-teal-500/50">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 py-2 pl-2">{t('documents.field')}</th>
                <th className="text-left text-xs font-medium text-gray-500 py-2 pl-2">{t('documents.value')}</th>
                <th className="w-12 text-right text-xs font-medium text-gray-500 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTemplateFields.map((key) => {
                let value = documentData[key] || '';
                let displayValue: string | JSX.Element = value;
                
                // Fizetési határidő előkészítése szerkesztéshez
                if (isPaymentDueField(key) && typeof value === 'string' && value.trim().endsWith(' nap')) {
                  value = preparePaymentDueForEdit(value);
                }
                
                // Formázott érték megjelenítése fogyasztás mezőknél
                if (isConsumptionField(key) && value) {
                  displayValue = formatConsumption(value);
                }
                
                // Special handling for price unit display if needed
                if (isPriceUnitField(key)) {
                  // Check all possible fields for a valid value
                  const priceUnitKeys = ['priceUnit', 'Ajánlati ár HUF/kWh-EUR/MWh', 'Ajánlati ár HUF/kWh-EUR/ MWh'];
                  let validValue = value;
                  
                  if (value !== 'HUF/kWh' && value !== 'EUR/MWh') {
                    // Try to find a valid value in any of the price unit fields
                    for (const priceKey of priceUnitKeys) {
                      if (documentData[priceKey] && 
                          (documentData[priceKey] === 'HUF/kWh' || documentData[priceKey] === 'EUR/MWh')) {
                        validValue = documentData[priceKey];
                        break;
                      }
                    }
                  }
                  
                  // If field is empty or not a valid option, show helper text
                  if (!validValue || (validValue !== 'HUF/kWh' && validValue !== 'EUR/MWh')) {
                    displayValue = <span className="text-gray-400 italic">Válasszon: HUF/kWh vagy EUR/MWh</span>;
                    // Also add a highlight to indicate it needs attention
                    displayValue = <span 
                      onClick={() => handleEditField(key, value)} 
                      className="bg-yellow-50 px-2 py-1 rounded border border-yellow-200 inline-block cursor-pointer hover:bg-yellow-100 transition-colors flex items-center"
                    >
                      {displayValue}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>;
                  } else {
                    // Add a highlight for valid values
                    displayValue = <span 
                      onClick={() => handleEditField(key, validValue)} 
                      className="bg-green-50 px-2 py-1 rounded border border-green-200 inline-block cursor-pointer hover:bg-green-100 transition-colors flex items-center"
                    >
                      {validValue}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>;
                  }
                }
                
                return (
                  <tr key={key} className="hover:bg-gray-50">
                    {editingKey === key ? (
                      <>
                        <td className="py-2 pl-2 align-top font-medium text-teal-600 text-xs sm:text-sm">{key}</td>
                        <td className="py-2 pl-2">
                          {isPriceUnitField(key) ? (
                            <select 
                              value={editingValue || ''}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-full bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-800"
                            >
                              <option value="">Válasszon...</option>
                              <option value="HUF/kWh">HUF/kWh</option>
                              <option value="EUR/MWh">EUR/MWh</option>
                            </select>
                          ) : (
                            <textarea
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="w-full h-24 bg-white border border-gray-300 rounded px-2 py-1 text-sm text-gray-800"
                            />
                          )}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={handleSaveEdit}
                              className="p-1 rounded-full hover:bg-teal-100 text-teal-600"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1 rounded-full hover:bg-red-100 text-red-600"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 pl-2 font-medium text-gray-700 text-xs sm:text-sm">{key}</td>
                        <td className="py-2 pl-2 text-gray-600 whitespace-pre-wrap">
                          {typeof displayValue === 'object' ? displayValue : (displayValue || <span className="text-gray-400 italic">{t('documents.empty')}</span>)}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => handleEditField(key, value)}
                            className="p-1 rounded-full hover:bg-teal-100 text-teal-600"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sablon előnézet panel */}
      <div className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200 lg:w-1/2 w-full flex flex-col min-h-[400px] lg:min-h-0 lg:max-h-full shadow-sm">
        <div className="text-sm font-medium text-gray-800 mb-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div className="flex items-center justify-end w-full space-x-2">
            <button
              onClick={generatePreview}
              disabled={isGeneratingPreview || !selectedTemplate}
              className={`flex items-center text-xs rounded-md px-2 py-1 ${isGeneratingPreview || !selectedTemplate
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`}
            >
              {isGeneratingPreview ? (
                <>
                  <LoadingSpinner size="sm" className="mr-1" />
                  <span>{t('documents.generating')}</span>
                </>
              ) : (
                <>
                  <EyeIcon className="h-3.5 w-3.5 mr-1" />
                  <span>{showPreview ? t('documents.regeneratePreview') : t('documents.generatePreview')}</span>
                </>
              )}
            </button>
          </div>

        </div>
        <DocumentPreview
          selectedTemplate={previewUrl ? {
            fileUrl: previewUrl,
            fileName: previewFileNameRef.current
          } : null}
          isLoadingPreview={isLoadingPreview}
          setIsLoadingPreview={setIsLoadingPreview}
        />
      </div>

      {/* POD Table Viewer Modal */}
      <PodTableViewer
        isOpen={isPodTableViewerOpen}
        onClose={() => setIsPodTableViewerOpen(false)}
        podTableData={documentData['POD_TABLE'] || ''}
      />
    </div>
  );
};

export default EditDataPage;
